import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Mail, MapPin, Scale } from "lucide-react";

const Section = ({ overline, title, children }) => (
  <div className="mb-8">
    {overline && (
      <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37] mb-2">{overline}</p>
    )}
    <h3 className="font-serif text-2xl text-white mb-3">{title}</h3>
    <div className="font-sans text-sm text-slate-300 leading-relaxed space-y-3">{children}</div>
  </div>
);

const Bullet = ({ children }) => (
  <li className="flex gap-3 items-start">
    <span className="mt-2 w-1 h-1 rounded-full bg-[#D4AF37] shrink-0" />
    <span>{children}</span>
  </li>
);

export default function LegalDialog({ open, defaultTab = "refund", onOpenChange }) {
  const [tab, setTab] = useState(defaultTab);

  // Sync external defaultTab on open
  React.useEffect(() => {
    if (open) setTab(defaultTab);
  }, [open, defaultTab]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="legal-modal"
        className="max-w-4xl bg-[#0B0E14] border border-[#D4AF37]/20 text-white p-0 overflow-hidden"
      >
        <DialogHeader className="px-8 pt-8 pb-2">
          <div className="flex items-center gap-3">
            <Scale className="w-5 h-5 text-[#D4AF37]" />
            <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37]">legal</p>
          </div>
          <DialogTitle className="font-serif text-3xl text-white mt-2">
            Kama <span className="gold-text">Loka</span> Journeys
          </DialogTitle>
          <DialogDescription className="font-sans text-sm text-slate-400 mt-1 flex items-center gap-2">
            <MapPin className="w-3 h-3" /> Queensland, Australia
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="px-8 pb-8">
          <TabsList
            className="grid grid-cols-2 bg-white/[0.03] border border-white/10 rounded-full p-1 h-auto w-full max-w-md mx-auto"
            data-testid="legal-tabs"
          >
            <TabsTrigger
              value="refund"
              data-testid="tab-refund"
              className="rounded-full font-sans text-xs uppercase tracking-overline data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-slate-300 py-2"
            >
              Refund policy
            </TabsTrigger>
            <TabsTrigger
              value="terms"
              data-testid="tab-terms"
              className="rounded-full font-sans text-xs uppercase tracking-overline data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-slate-300 py-2"
            >
              Terms & conditions
            </TabsTrigger>
          </TabsList>

          <div className="mt-8 max-h-[65vh] overflow-y-auto pr-3">
            {/* REFUND POLICY */}
            <TabsContent value="refund" data-testid="content-refund" className="mt-0 focus:outline-none">
              <p className="font-sans text-sm text-slate-300 leading-relaxed mb-8">
                Kama Loka Journeys provides a professional complementary therapy service delivered with care,
                clarity, and integrity, in accordance with Australian Consumer Law.
              </p>

              <Section overline="01" title="Consumer rights">
                <p>
                  You are entitled to a remedy if a service is not delivered with due care and skill, is not as
                  described, or is not reasonably fit for its intended purpose.
                </p>
                <p>
                  As services are experiential and personal in nature, outcomes will vary. Refunds are not provided
                  for change of mind, non-attendance, or dissatisfaction based on personal perception of the
                  experience, unless required under Australian Consumer Law.
                </p>
              </Section>

              <Section overline="02" title="Cancellations">
                <ul className="space-y-2">
                  <Bullet>
                    <span className="text-white font-medium">48+ hours' notice:</span> Reschedule permitted.
                  </Bullet>
                  <Bullet>
                    <span className="text-white font-medium">24–48 hours' notice:</span> One reschedule permitted
                    (no refunds).
                  </Bullet>
                  <Bullet>
                    <span className="text-white font-medium">Less than 24 hours or non-attendance:</span> Full
                    session fee is retained.
                  </Bullet>
                </ul>
                <p className="text-slate-400 italic">Late arrivals may result in a shortened session.</p>
              </Section>

              <Section overline="03" title="Practitioner changes">
                <p>
                  If your session is rescheduled by Kassie, you will be offered an alternative time or a full refund.
                </p>
              </Section>

              <Section overline="04" title="Nature of services">
                <p>
                  Sessions are introspective and non-tangible in nature. Experiences and outcomes vary between
                  individuals.
                </p>
                <p>
                  Refunds are not provided based on personal interpretation, emotional response, or perceived lack
                  of results, unless required under Australian Consumer Law.
                </p>
              </Section>

              <div className="mt-10 p-5 rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5">
                <p className="font-sans text-[10px] uppercase tracking-overline text-[#D4AF37] mb-2">enquiries</p>
                <a
                  href="mailto:kassie@kamalokajourneys.com.au"
                  className="font-serif text-xl text-white hover:text-[#D4AF37] transition-colors flex items-center gap-2"
                  data-testid="legal-email"
                >
                  <Mail className="w-4 h-4" /> kassie@kamalokajourneys.com.au
                </a>
              </div>
            </TabsContent>

            {/* TERMS & CONDITIONS */}
            <TabsContent value="terms" data-testid="content-terms" className="mt-0 focus:outline-none">
              <p className="font-sans text-sm text-slate-300 leading-relaxed mb-8">
                By booking a session with Kama Loka Journeys, you agree to the following terms.
              </p>

              <Section overline="01" title="Services">
                <p>
                  Kama Loka Journeys offers complementary therapy services, including Past Life Regression and Soul
                  Regression. These sessions are intended to support personal insight and self-exploration.
                </p>
                <p className="text-slate-400 italic">
                  They are not a substitute for medical, psychological, or psychiatric care.
                </p>
              </Section>

              <Section overline="02" title="Client responsibility">
                <p>By booking, you confirm that you:</p>
                <ul className="space-y-2">
                  <Bullet>Are over 18 years of age.</Bullet>
                  <Bullet>Are participating voluntarily.</Bullet>
                  <Bullet>Take responsibility for your physical, emotional, and mental wellbeing.</Bullet>
                </ul>
                <p>You agree to provide accurate and relevant information where required.</p>
              </Section>

              <Section overline="03" title="Booking & payment">
                <p>
                  All sessions must be paid in full at the time of booking unless otherwise agreed. Bookings are
                  confirmed upon receipt of payment.
                </p>
              </Section>

              <Section overline="04" title="Cancellations">
                <p>Cancellations and rescheduling are subject to the Refund Policy.</p>
              </Section>

              <Section overline="05" title="Nature of services">
                <p>
                  Sessions involve guided hypnosis and altered states of awareness. Individual experiences vary,
                  and outcomes cannot be guaranteed.
                </p>
              </Section>

              <Section overline="06" title="Limitation of liability">
                <p>Nothing in these terms excludes or limits your rights under Australian Consumer Law.</p>
                <p>
                  To the extent permitted by law, Kama Loka Journeys is not liable for outcomes arising from
                  participation in sessions where services have been provided with due care and skill.
                </p>
              </Section>

              <Section overline="07" title="Privacy">
                <p>
                  Personal information is handled in accordance with applicable Australian privacy laws. Further
                  details are outlined in the Privacy Policy.
                </p>
              </Section>

              <Section overline="08" title="Acceptance">
                <p>
                  By proceeding with a booking, you acknowledge that you have read and agree to these Terms &
                  Conditions.
                </p>
              </Section>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
